import React from "react";
import { useNavigate, useParams } from "react-router";
import { Link, LongTextLabel } from "@sscs/ui";
import { Dialog } from "Components";
import { INSIGHTS_ROUTE, INSIGHTS_SEARCH_ROUTE } from "Constants";
import { useAppDispatch, useDialog } from "Hooks";
import { useGetWhoamiQuery } from "Services";
import useSearch from "../../hooks/useSearch";
import { clearGroupFilters } from "../../store";
import { MyInsightsSearch, QuickInsightsSearch } from "../../types";
import styles from "./navigation.module.scss";

type LabelProps = {
  search: MyInsightsSearch | QuickInsightsSearch;
};

const Label = ({ search }: LabelProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { groupId } = useParams();
  const { isDirty } = useSearch();
  const { data: whoami } = useGetWhoamiQuery();
  const unsavedChangesDialog = useDialog();
  const changeGroupDialog = useDialog();

  // safe to cast since group can be null even on MyInsightsSearch
  // Also simpler than having type guard and more complex condition
  const searchGroupId = (search as MyInsightsSearch).group;
  const searchGroup = whoami?.org_groups.find(
    (group) => group.group_id === searchGroupId,
  );
  const navigationLink = `/${INSIGHTS_ROUTE}/group/${searchGroupId ?? groupId}/${INSIGHTS_SEARCH_ROUTE}/${search.id}`;

  return (
    <>
      <Link
        to={navigationLink}
        underline="none"
        onClick={(event) => {
          if (searchGroupId && searchGroupId !== groupId) {
            event.preventDefault();
            changeGroupDialog.open();
          } else if (isDirty) {
            event.preventDefault();
            unsavedChangesDialog.open();
          } else {
            dispatch(clearGroupFilters(groupId));
          }
        }}
      >
        <LongTextLabel label={search.name} />
      </Link>
      <Dialog
        title={
          <div className={styles["dialog-title"]}>
            Navigate to&nbsp;
            <LongTextLabel
              label={search.name}
              className={styles["search-name"]}
            />
            &nbsp;search
          </div>
        }
        confirmText="Navigate"
        cancelText="No"
        onConfirm={() => {
          dispatch(clearGroupFilters(groupId));
          navigate(navigationLink);
          unsavedChangesDialog.close();
        }}
        onCancel={unsavedChangesDialog.close}
        isOpen={unsavedChangesDialog.isOpen}
        onClose={unsavedChangesDialog.close}
        cancelButtonColor="primary"
        confirmButtonColor="secondary"
      >
        <span>
          Are you sure you want to navigate to {search.name} search? Unsaved
          changes will be lost.
        </span>
      </Dialog>
      <Dialog
        title={
          <div className={styles["dialog-title"]}>Change current group</div>
        }
        confirmText="Change group"
        cancelText="No"
        onConfirm={() => {
          dispatch(clearGroupFilters(groupId));
          navigate(navigationLink);
          changeGroupDialog.close();
        }}
        onCancel={changeGroupDialog.close}
        isOpen={changeGroupDialog.isOpen}
        onClose={changeGroupDialog.close}
        cancelButtonColor="primary"
        confirmButtonColor="secondary"
      >
        <span>
          Are you sure you want to switch the currently selected group to{" "}
          <b>{searchGroup?.name}</b>?{" "}
          {isDirty && <>Unsaved changes will be lost.</>}
        </span>
      </Dialog>
    </>
  );
};

export default Label;
